import { useState } from 'react';
import {
  Clock,
  FileX,
  MessageSquare,
  Mail,
  PhoneCall,
  CheckCircle2,
  Search,
  X,
  RotateCcw,
  Loader2,
  History,
  Sparkles,
  ShieldAlert,
} from 'lucide-react';
import { Button, ProgressBar } from '@lms/ui';
import { useCrmParticipants } from '../hooks/useCrm';
import { usePrograms } from '../hooks/usePrograms';
import { OutreachModal } from '../components/crm/OutreachModal';
import { OutreachHistoryModal } from '../components/crm/OutreachHistoryModal';
import { Pagination } from '../components/Pagination';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import type {
  CrmParticipantItem,
  OutreachType,
  OutreachChannel,
  OutreachStatus,
} from '@lms/shared/types';

export function ParticipantCRM() {
  const { language, t } = useLanguage();
  const { data: programsData } = usePrograms({ limit: 100 });
  const programs = Array.isArray(programsData?.items) ? programsData.items : [];

  // Filter States
  const [search, setSearch] = useState('');
  const [inactivityType, setInactivityType] = useState<string>('ALL');
  const [outreachStatus, setOutreachStatus] = useState<string>('ALL');
  const [selectedProgramId, setSelectedProgramId] = useState<string>('ALL');
  const [selectedGender, setSelectedGender] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Modal States
  const [selectedParticipantForOutreach, setSelectedParticipantForOutreach] =
    useState<CrmParticipantItem | null>(null);
  const [outreachModalTab, setOutreachModalTab] = useState<'WHATSAPP' | 'EMAIL' | 'MANUAL'>(
    'WHATSAPP'
  );
  const [outreachModalType, setOutreachModalType] = useState<OutreachType>('INACTIVITY_REMINDER');

  const [selectedParticipantForHistory, setSelectedParticipantForHistory] =
    useState<CrmParticipantItem | null>(null);

  // Fetch CRM Data
  const {
    data: crmData,
    isLoading,
    refetch,
    isFetching,
  } = useCrmParticipants({
    inactivityType: inactivityType !== 'ALL' ? inactivityType : undefined,
    outreachStatus: outreachStatus !== 'ALL' ? outreachStatus : undefined,
    programId: selectedProgramId !== 'ALL' ? selectedProgramId : undefined,
    gender: selectedGender !== 'ALL' ? selectedGender : undefined,
    search: search.trim() || undefined,
    page: currentPage,
    limit: pageSize,
  });

  const participants = crmData?.items || [];
  const metrics = crmData?.metrics || {
    totalAtRisk: 0,
    inactiveLogin7Days: 0,
    inactiveLogin14Days: 0,
    inactiveLogin30Days: 0,
    missingExams: 0,
    contactedCount: 0,
    contactedPercentage: 0,
    resolvedCount: 0,
    resolvedPercentage: 0,
  };

  const totalPages = crmData?.totalPages || 1;

  const hasActiveFilters =
    search.trim().length > 0 ||
    inactivityType !== 'ALL' ||
    outreachStatus !== 'ALL' ||
    selectedProgramId !== 'ALL' ||
    selectedGender !== 'ALL';

  const resetFilters = () => {
    setSearch('');
    setInactivityType('ALL');
    setOutreachStatus('ALL');
    setSelectedProgramId('ALL');
    setSelectedGender('ALL');
    setCurrentPage(1);
  };

  const openOutreach = (
    participant: CrmParticipantItem,
    tab: 'WHATSAPP' | 'EMAIL' | 'MANUAL',
    type: OutreachType
  ) => {
    setSelectedParticipantForOutreach(participant);
    setOutreachModalTab(tab);
    setOutreachModalType(type);
  };

  const getOutreachStatusBadge = (status?: OutreachStatus | null) => {
    if (!status) {
      return (
        <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-surface-container-high text-on-surface-variant/80">
          <Bilingual {...copy.crm.neverContacted} />
        </span>
      );
    }
    switch (status) {
      case 'RESOLVED':
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-success/15 text-success">
            {t(copy.crm.filterOutreachResolved)}
          </span>
        );
      case 'REPLIED':
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-sky-500/15 text-sky-600">
            {t(copy.crm.filterOutreachReplied)}
          </span>
        );
      case 'NO_RESPONSE':
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-error/15 text-error">
            {t(copy.crm.filterOutreachNoResponse)}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-primary/10 text-primary">
            {t(copy.crm.filterOutreachContacted)}
          </span>
        );
    }
  };

  const getChannelIcon = (channel?: OutreachChannel | null) => {
    switch (channel) {
      case 'WHATSAPP':
        return <MessageSquare size={14} className="text-emerald-600" />;
      case 'EMAIL':
        return <Mail size={14} className="text-sky-600" />;
      case 'PHONE':
        return <PhoneCall size={14} className="text-primary" />;
      default:
        return null;
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-8 animate-fadeIn pb-16">
      {/* Editorial Header */}
      <section className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1.5 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-headline font-bold uppercase tracking-wider">
            <Sparkles size={12} />
            <span>Retensi & Engagement Peserta</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-headline font-extrabold text-on-surface tracking-tight m-0">
            <Bilingual {...copy.crm.pageTitle} />
          </h1>
          <p className="text-sm sm:text-base font-body text-on-surface-variant leading-relaxed">
            <Bilingual {...copy.crm.pageSubtitle} />
          </p>
        </div>

        <div className="flex items-center gap-3 self-stretch sm:self-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            leftIcon={<RotateCcw size={14} className={isFetching ? 'animate-spin' : ''} />}
            className="text-xs font-bold uppercase tracking-wider rounded-xl bg-surface-container-low hover:bg-surface-container"
          >
            Segarkan Data
          </Button>
        </div>
      </section>

      {/* KPI Metric Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Total At Risk */}
        <div className="p-5 rounded-2xl bg-surface-container-low border border-outline-variant/10 flex flex-col justify-between space-y-3">
          <div className="flex items-center justify-between text-on-surface-variant">
            <span className="text-[11px] font-headline font-bold uppercase tracking-wider">
              <Bilingual {...copy.crm.metricTotalAtRisk} />
            </span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center">
              <ShieldAlert size={16} />
            </div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-headline font-black text-on-surface">
              {metrics.totalAtRisk}
            </div>
            <span className="text-[10px] text-on-surface-variant opacity-70">
              Perlu pendampingan
            </span>
          </div>
        </div>

        {/* Inactive > 7 Days */}
        <div className="p-5 rounded-2xl bg-surface-container-low border border-outline-variant/10 flex flex-col justify-between space-y-3">
          <div className="flex items-center justify-between text-on-surface-variant">
            <span className="text-[11px] font-headline font-bold uppercase tracking-wider">
              <Bilingual {...copy.crm.metricInactive7Days} />
            </span>
            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Clock size={16} />
            </div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-headline font-black text-primary">
              {metrics.inactiveLogin7Days}
            </div>
            <span className="text-[10px] text-on-surface-variant opacity-70">
              &gt; 1 pekan tidak login
            </span>
          </div>
        </div>

        {/* Missing Exams */}
        <div className="p-5 rounded-2xl bg-surface-container-low border border-outline-variant/10 flex flex-col justify-between space-y-3">
          <div className="flex items-center justify-between text-on-surface-variant">
            <span className="text-[11px] font-headline font-bold uppercase tracking-wider">
              <Bilingual {...copy.crm.metricMissingExams} />
            </span>
            <div className="w-8 h-8 rounded-xl bg-error/10 text-error flex items-center justify-center">
              <FileX size={16} />
            </div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-headline font-black text-error">
              {metrics.missingExams}
            </div>
            <span className="text-[10px] text-on-surface-variant opacity-70">
              Ujian belum dicoba
            </span>
          </div>
        </div>

        {/* Contacted % */}
        <div className="p-5 rounded-2xl bg-surface-container-low border border-outline-variant/10 flex flex-col justify-between space-y-3">
          <div className="flex items-center justify-between text-on-surface-variant">
            <span className="text-[11px] font-headline font-bold uppercase tracking-wider">
              <Bilingual {...copy.crm.metricContacted} />
            </span>
            <div className="w-8 h-8 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center">
              <MessageSquare size={16} />
            </div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-headline font-black text-sky-600">
              {metrics.contactedPercentage}%
            </div>
            <span className="text-[10px] text-on-surface-variant opacity-70">
              {metrics.contactedCount} dari {metrics.totalAtRisk} peserta
            </span>
          </div>
        </div>

        {/* Resolved / Re-engaged % */}
        <div className="p-5 rounded-2xl bg-surface-container-low border border-outline-variant/10 flex flex-col justify-between space-y-3 col-span-2 md:col-span-1">
          <div className="flex items-center justify-between text-on-surface-variant">
            <span className="text-[11px] font-headline font-bold uppercase tracking-wider">
              <Bilingual {...copy.crm.metricResolved} />
            </span>
            <div className="w-8 h-8 rounded-xl bg-success/10 text-success flex items-center justify-center">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-headline font-black text-success">
              {metrics.resolvedPercentage}%
            </div>
            <span className="text-[10px] text-on-surface-variant opacity-70">
              {metrics.resolvedCount} kasus tuntas
            </span>
          </div>
        </div>
      </div>

      {/* Toolbar Filters */}
      <div className="p-5 rounded-2xl bg-surface-container-lowest border border-outline-variant/10 shadow-level1 space-y-4">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-40 rtl:left-auto rtl:right-4"
            />
            <input
              type="text"
              placeholder={t(copy.crm.searchPlaceholder)}
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low border-none rounded-xl pl-11 pr-10 rtl:pl-10 rtl:pr-11 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all h-11"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setCurrentPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-on-surface-variant hover:text-primary rounded-full hover:bg-surface-container-high transition-colors rtl:right-auto rtl:left-3"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-error hover:bg-error/10 rounded-xl transition-colors shrink-0"
            >
              <RotateCcw size={14} />
              <span>Reset Filter</span>
            </button>
          )}
        </div>

        {/* Dropdown Filters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
          {/* Inactivity Type */}
          <div className="space-y-1">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
              Kategori Inaktivitas
            </label>
            <select
              value={inactivityType}
              onChange={e => {
                setInactivityType(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low rounded-xl px-3 py-2 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none h-10"
            >
              <option value="ALL">{t(copy.crm.filterInactivityAll)}</option>
              <option value="NO_LOGIN_7_DAYS">{t(copy.crm.filterInactivity7Days)}</option>
              <option value="NO_LOGIN_14_DAYS">{t(copy.crm.filterInactivity14Days)}</option>
              <option value="NO_LOGIN_30_DAYS">{t(copy.crm.filterInactivity30Days)}</option>
              <option value="NO_EXAM">{t(copy.crm.filterInactivityNoExam)}</option>
              <option value="STALLED_PROGRESS">{t(copy.crm.filterInactivityStalled)}</option>
            </select>
          </div>

          {/* Outreach Status */}
          <div className="space-y-1">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
              Status Riwayat Kontak
            </label>
            <select
              value={outreachStatus}
              onChange={e => {
                setOutreachStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low rounded-xl px-3 py-2 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none h-10"
            >
              <option value="ALL">{t(copy.crm.filterOutreachAll)}</option>
              <option value="NEVER_CONTACTED">{t(copy.crm.filterOutreachNever)}</option>
              <option value="CONTACTED">{t(copy.crm.filterOutreachContacted)}</option>
              <option value="REPLIED">{t(copy.crm.filterOutreachReplied)}</option>
              <option value="RESOLVED">{t(copy.crm.filterOutreachResolved)}</option>
              <option value="NO_RESPONSE">{t(copy.crm.filterOutreachNoResponse)}</option>
            </select>
          </div>

          {/* Program Filter */}
          <div className="space-y-1">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
              Program Belajar
            </label>
            <select
              value={selectedProgramId}
              onChange={e => {
                setSelectedProgramId(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low rounded-xl px-3 py-2 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none h-10"
            >
              <option value="ALL">Semua Program</option>
              {programs.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </div>

          {/* Gender Filter */}
          <div className="space-y-1">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
              Gender Peserta
            </label>
            <select
              value={selectedGender}
              onChange={e => {
                setSelectedGender(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low rounded-xl px-3 py-2 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none h-10"
            >
              <option value="ALL">Semua Gender</option>
              <option value="MALE">Ikhwan (Laki-laki)</option>
              <option value="FEMALE">Akhwat (Perempuan)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Participants Matrix Table */}
      <div className="bg-surface-container-lowest rounded-2xl shadow-level1 border border-on-surface/5 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-3">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="text-xs font-body text-on-surface-variant">
              Menganalisis status keaktifan peserta...
            </span>
          </div>
        ) : participants.length === 0 ? (
          <div className="text-center py-20 space-y-3 p-6">
            <div className="w-14 h-14 rounded-full bg-success/10 text-success mx-auto flex items-center justify-center">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="text-base font-headline font-bold text-on-surface m-0">
              <Bilingual {...copy.crm.emptyTitle} />
            </h3>
            <p className="text-xs font-body text-on-surface-variant max-w-md mx-auto m-0 leading-relaxed">
              <Bilingual {...copy.crm.emptyDesc} />
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-surface-container-low/70 text-on-surface-variant border-b border-outline-variant/10 text-[10px] font-label font-bold uppercase tracking-wider">
                  <th className="px-6 py-4">
                    <Bilingual {...copy.crm.tableHeaderParticipant} />
                  </th>
                  <th className="px-6 py-4">
                    <Bilingual {...copy.crm.tableHeaderPrograms} />
                  </th>
                  <th className="px-6 py-4">
                    <Bilingual {...copy.crm.tableHeaderInactivity} />
                  </th>
                  <th className="px-6 py-4">
                    <Bilingual {...copy.crm.tableHeaderOutreach} />
                  </th>
                  <th className="px-6 py-4 text-center">
                    <Bilingual {...copy.crm.tableHeaderActions} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10 text-xs font-body">
                {participants.map(p => (
                  <tr
                    key={p.id}
                    className="hover:bg-surface-container-low/40 transition-colors group"
                  >
                    {/* Participant Details */}
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-headline font-bold text-sm text-on-surface group-hover:text-primary transition-colors">
                            {p.name}
                          </span>
                          {p.gender && (
                            <span
                              className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                                p.gender === 'FEMALE'
                                  ? 'bg-purple-500/10 text-purple-600'
                                  : 'bg-blue-500/10 text-blue-600'
                              }`}
                            >
                              {p.gender === 'FEMALE' ? 'Akhwat' : 'Ikhwan'}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-on-surface-variant opacity-80 flex flex-col">
                          <span>{p.email}</span>
                          {p.phone && <span>WA: {p.phone}</span>}
                          {p.nip && <span className="text-primary font-bold">NIP: {p.nip}</span>}
                        </div>
                      </div>
                    </td>

                    {/* Enrolled Programs */}
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-2 max-w-xs">
                        {p.enrolledPrograms.length === 0 ? (
                          <span className="text-[11px] text-on-surface-variant italic">
                            Belum ada program aktif
                          </span>
                        ) : (
                          p.enrolledPrograms.map(enr => (
                            <div key={enr.id} className="space-y-1">
                              <div className="flex items-center justify-between text-[11px]">
                                <span className="font-bold text-on-surface truncate">
                                  {enr.name}
                                </span>
                                <span className="font-mono text-primary font-bold pl-2">
                                  {Math.round(enr.progress)}%
                                </span>
                              </div>
                              <ProgressBar value={enr.progress} size="sm" />
                            </div>
                          ))
                        )}
                      </div>
                    </td>

                    {/* Inactivity Status & Badges */}
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-1.5">
                        {/* Login Inactivity */}
                        <div className="flex items-center gap-1.5">
                          {p.daysSinceLastLogin != null ? (
                            <span
                              className={`px-2 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1 ${
                                p.daysSinceLastLogin >= 30
                                  ? 'bg-error/15 text-error'
                                  : p.daysSinceLastLogin >= 14
                                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                                    : p.daysSinceLastLogin >= 7
                                      ? 'bg-primary/10 text-primary'
                                      : 'bg-surface-container-high text-on-surface-variant'
                              }`}
                            >
                              <Clock size={12} />
                              <span>
                                {p.daysSinceLastLogin === 0
                                  ? 'Aktif hari ini'
                                  : `Tidak login ${p.daysSinceLastLogin} hari`}
                              </span>
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-error/15 text-error">
                              <Bilingual {...copy.crm.neverLoggedIn} />
                            </span>
                          )}
                        </div>

                        {/* Missed Exams Badge */}
                        {p.missedEvaluations.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-error/15 text-error flex items-center gap-1">
                              <FileX size={12} />
                              <span>
                                {p.missedEvaluations.length} {t(copy.crm.missedExamsCount)}
                              </span>
                            </span>
                          </div>
                        )}

                        {/* Stalled Progress Badge */}
                        {p.inactivityFlags.isStalledProgress && !p.inactivityFlags.noLogin7Days && (
                          <div className="flex items-center gap-1.5">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-500/15 text-amber-700 dark:text-amber-400">
                              <Bilingual {...copy.crm.stalledProgressBadge} />
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Last Outreach Summary */}
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-1.5 max-w-xs">
                        <div className="flex items-center gap-2">
                          {getOutreachStatusBadge(p.outreachSummary.lastStatus)}
                          {p.outreachSummary.lastChannel && (
                            <span
                              className="p-1 rounded bg-surface-container-high"
                              title={p.outreachSummary.lastChannel}
                            >
                              {getChannelIcon(p.outreachSummary.lastChannel)}
                            </span>
                          )}
                        </div>

                        {p.outreachSummary.lastContactDate && (
                          <div className="text-[10px] text-on-surface-variant font-mono">
                            <span>{formatDate(p.outreachSummary.lastContactDate)}</span>
                            {p.outreachSummary.lastAdminName && (
                              <span> • oleh {p.outreachSummary.lastAdminName}</span>
                            )}
                          </div>
                        )}

                        {p.outreachSummary.lastNotes && (
                          <p className="text-[11px] text-on-surface-variant/90 line-clamp-2 italic m-0 bg-surface-container-low/50 p-1.5 rounded-lg">
                            &quot;{p.outreachSummary.lastNotes}&quot;
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Quick Actions Bar */}
                    <td className="px-6 py-4 align-middle text-center">
                      <div className="flex items-center justify-center gap-2">
                        {/* WhatsApp Quick Trigger */}
                        <button
                          type="button"
                          onClick={() =>
                            openOutreach(
                              p,
                              'WHATSAPP',
                              p.missedEvaluations.length > 0
                                ? 'EXAM_REMINDER'
                                : 'INACTIVITY_REMINDER'
                            )
                          }
                          disabled={!p.phone}
                          className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                          title={p.phone ? 'Hubungi via WhatsApp' : 'Nomor telepon tidak tersedia'}
                        >
                          <MessageSquare size={16} />
                        </button>

                        {/* Email Quick Trigger */}
                        <button
                          type="button"
                          onClick={() =>
                            openOutreach(
                              p,
                              'EMAIL',
                              p.missedEvaluations.length > 0
                                ? 'EXAM_REMINDER'
                                : 'INACTIVITY_REMINDER'
                            )
                          }
                          className="p-2 rounded-xl bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 transition-all"
                          title="Kirim Pengingat Email"
                        >
                          <Mail size={16} />
                        </button>

                        {/* Log Manual / Phone Call */}
                        <button
                          type="button"
                          onClick={() => openOutreach(p, 'MANUAL', 'GENERAL_FOLLOWUP')}
                          className="p-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                          title="Catat Panggilan / Hasil Kontak"
                        >
                          <PhoneCall size={16} />
                        </button>

                        {/* History Timeline Trigger */}
                        <button
                          type="button"
                          onClick={() => setSelectedParticipantForHistory(p)}
                          className="p-2 rounded-xl bg-surface-container-high text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-all"
                          title="Lihat Riwayat Interaksi"
                        >
                          <History size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {participants.length > 0 && (
          <div className="p-4 border-t border-outline-variant/10">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* Outreach Action Modal */}
      {selectedParticipantForOutreach && (
        <OutreachModal
          isOpen={!!selectedParticipantForOutreach}
          onClose={() => setSelectedParticipantForOutreach(null)}
          participant={selectedParticipantForOutreach}
          initialTab={outreachModalTab}
          initialType={outreachModalType}
        />
      )}

      {/* Outreach History Timeline Modal */}
      {selectedParticipantForHistory && (
        <OutreachHistoryModal
          isOpen={!!selectedParticipantForHistory}
          onClose={() => setSelectedParticipantForHistory(null)}
          participant={selectedParticipantForHistory}
        />
      )}
    </div>
  );
}
